/**
 * Bonus Service
 *
 * Tracks watched-ad bonus tokens and the active 1.5× session multiplier.
 * Persistent state lives in PlayerStore; session bonus is in-memory only.
 */

import type { PlayerStore } from '@/state/playerStore';
import {
  type AdWatchResult,
  type BonusState,
  DEFAULT_BONUS_SETTINGS,
  type GameSessionBonus,
  INITIAL_SESSION_BONUS,
} from './types';

export interface BonusService {
  subscribe(listener: (state: BonusState) => void): () => void;
  getBonusState(): BonusState;
  getBonusSettings(): typeof DEFAULT_BONUS_SETTINGS;
  canWatchAd(): boolean;
  getRemainingCooldown(): number;
  watchAd(): AdWatchResult;
  activateBonus(): boolean;
  getSessionBonus(): GameSessionBonus;
  calculateBonusScore(baseScore: number): number;
  endGameSession(): void;
  resetBonusState(): void;
}

export function createBonusService(store: PlayerStore): BonusService {
  const settings = { ...DEFAULT_BONUS_SETTINGS };
  let sessionBonus: GameSessionBonus = { ...INITIAL_SESSION_BONUS };
  const listeners = new Set<(state: BonusState) => void>();

  store.subscribe((save) => {
    for (const listener of listeners) listener({ ...save.bonus });
  });

  const read = (): BonusState => store.get().bonus;
  const writeBonus = (next: BonusState): void => {
    store.setBonus(next);
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    getBonusState() {
      return { ...read() };
    },

    getBonusSettings() {
      return { ...settings };
    },

    canWatchAd() {
      const cur = read();
      const cooldownMs = settings.adCooldownMinutes * 60 * 1000;
      return Date.now() - cur.lastAdWatchTime >= cooldownMs;
    },

    getRemainingCooldown() {
      if (this.canWatchAd()) return 0;
      const cur = read();
      const cooldownMs = settings.adCooldownMinutes * 60 * 1000;
      const remaining = cooldownMs - (Date.now() - cur.lastAdWatchTime);
      if (remaining <= 0) return 0;
      if (remaining > cooldownMs * 10) return 0;
      return Math.ceil(remaining / (60 * 1000));
    },

    watchAd() {
      if (!this.canWatchAd()) {
        return {
          success: false,
          tokensEarned: 0,
          error: `Please wait ${this.getRemainingCooldown()} minutes before watching another ad.`,
        };
      }
      const cur = read();
      if (cur.availableTokens >= settings.maxTokens) {
        return {
          success: false,
          tokensEarned: 0,
          error: 'You already have the maximum number of tokens.',
        };
      }
      const tokensToEarn = Math.min(settings.tokensPerAd, settings.maxTokens - cur.availableTokens);
      writeBonus({
        ...cur,
        availableTokens: cur.availableTokens + tokensToEarn,
        totalTokensEarned: cur.totalTokensEarned + tokensToEarn,
        lastAdWatchTime: Date.now(),
        isUnlocked: true,
      });
      return { success: true, tokensEarned: tokensToEarn };
    },

    activateBonus() {
      const cur = read();
      if (cur.availableTokens <= 0 || cur.isBonusActive) return false;
      writeBonus({
        ...cur,
        availableTokens: cur.availableTokens - 1,
        totalTokensUsed: cur.totalTokensUsed + 1,
        isBonusActive: true,
      });
      sessionBonus = {
        isActive: true,
        multiplier: settings.bonusMultiplier,
        tokensUsed: 1,
        originalScore: 0,
        bonusScore: 0,
      };
      return true;
    },

    getSessionBonus() {
      return { ...sessionBonus };
    },

    calculateBonusScore(baseScore) {
      if (!read().isBonusActive) return baseScore;
      const bonusScore = Math.round(baseScore * settings.bonusMultiplier);
      sessionBonus = {
        ...sessionBonus,
        originalScore: sessionBonus.originalScore + baseScore,
        bonusScore: sessionBonus.bonusScore + bonusScore,
      };
      return bonusScore;
    },

    endGameSession() {
      const cur = read();
      if (cur.isBonusActive) {
        writeBonus({ ...cur, isBonusActive: false });
      }
      sessionBonus = { ...INITIAL_SESSION_BONUS };
    },

    resetBonusState() {
      writeBonus({
        availableTokens: 0,
        isBonusActive: false,
        isUnlocked: false,
        lastAdWatchTime: 0,
        totalTokensEarned: 0,
        totalTokensUsed: 0,
      });
      sessionBonus = { ...INITIAL_SESSION_BONUS };
    },
  };
}
